/**
 * Home slider of a custom shop template (Mizranit). Each slide is a picture that carries its own text.
 * `mobileImageUrl` is a portrait picture for phones; without it the wide one is cropped to the center.
 * `href` is optional: a category (`?section=<id>`), a product (`?product=<id>`), or a full address.
 */

export const SHOP_SLIDES_MAX = 12;

function cleanUrl(value) {
  const text = String(value ?? '').trim();
  return text.length > 2000 ? '' : text;
}

function slideId(raw, index) {
  const id = String(raw ?? '').trim();
  return id || `slide-${index + 1}`;
}

export function normalizeShopSlides(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const [index, item] of raw.entries()) {
    if (!item || typeof item !== 'object') continue;
    const imageUrl = cleanUrl(item.imageUrl ?? item.image_url);
    if (!imageUrl) continue;
    let id = slideId(item.id, index);
    while (seen.has(id)) id = `${id}-${index + 1}`;
    seen.add(id);
    out.push({
      id,
      imageUrl,
      mobileImageUrl: cleanUrl(item.mobileImageUrl ?? item.mobile_image_url),
      href: cleanUrl(item.href),
    });
    if (out.length >= SHOP_SLIDES_MAX) break;
  }
  return out;
}

export const SHOP_START_BANNER_LANGS = ['he', 'ru', 'en'];

export const SHOP_START_BANNER_FRAMES_MAX = 6;

function framePair(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  return {
    imageUrl: cleanUrl(source.imageUrl ?? source.image_url),
    mobileImageUrl: cleanUrl(source.mobileImageUrl ?? source.mobile_image_url),
  };
}

/** The main picture plus optional animation frames that briefly replace it (a yawn, a wink). */
function bannerImages(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const frames = (Array.isArray(source.frames) ? source.frames : [])
    .map(framePair)
    .filter((frame) => frame.imageUrl)
    .slice(0, SHOP_START_BANNER_FRAMES_MAX);
  return { ...framePair(source), frames };
}

/**
 * Full-screen picture that greets a visitor on the shop home, then fades away.
 * The base pair serves every language; a language pair replaces it for that language only.
 */
export function normalizeShopStartBanner(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const langSource = source.langImages && typeof source.langImages === 'object' ? source.langImages : {};
  const langImages = {};
  for (const code of SHOP_START_BANNER_LANGS) {
    const images = bannerImages(langSource[code]);
    if (images.imageUrl) langImages[code] = images;
  }
  return { ...bannerImages(source), langImages };
}

export function pickShopStartBanner(banner, lang) {
  const own = banner?.langImages?.[lang];
  if (own?.imageUrl) return own;
  return banner?.imageUrl
    ? { imageUrl: banner.imageUrl, mobileImageUrl: banner.mobileImageUrl, frames: banner.frames || [] }
    : null;
}
